import { useEffect, useState, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import { ChevronLeft, ChevronUp, ChevronDown, HelpCircle } from 'lucide-react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Footer } from '../Footer';
import { BaseModal } from '../Modal/BaseModal';
import { useAuth as useHubAuth } from '../../hooks/useAuth';
import {
  createMessageHandler,
  sendMessageToGame,
  getAuthToken,
  MessageType,
  isOriginAllowed,
} from '../../utils/gameMessaging';
import { apiClient } from '@/services/api-client';
import { useGameRegistry } from '@/hooks/useGameRegistry';
import { useGuestSession } from '@/hooks/useGuestSession';
import { GuestConversionCTA } from '../auth/GuestConversionCTA';
import { GuestCompletionPopup } from './GuestCompletionPopup';
import { Analytics } from '@/lib/analytics';

interface GameFrameProps {
  // Allow passing custom game URL for testing
  gameUrl?: string;
}

export default function GameFrame({ gameUrl }: GameFrameProps) {
  const { gameId, challengeId } = useParams<{ gameId: string; challengeId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoaded, isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const { isPremium, isPremiumLoaded } = useHubAuth();
  const { getGameById, loading: gamesLoading } = useGameRegistry();
  const guestSession = useGuestSession(isLoaded ?? false, isSignedIn ?? false, {
    gameId: gameId ?? undefined,
    challengeId: challengeId ?? undefined
  });
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const pendingReadyEvent = useRef<MessageEvent | null>(null);
  const [isGameLoading, setIsGameLoading] = useState(true);
  const [gameError, setGameError] = useState<string | null>(null);
  const [authSent, setAuthSent] = useState(false);
  const [isChallengeCompleted, setIsChallengeCompleted] = useState<boolean>(false);
  const [isCheckingCompletion, setIsCheckingCompletion] = useState<boolean>(true);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [showCompletionPopup, setShowCompletionPopup] = useState(false);
  const [hasDetectedCompletion, setHasDetectedCompletion] = useState(false);

  // Get game data from route state (passed from challenge popup or continuous game handler).
  // Fall back to registry lookup when state is missing (direct URL, page refresh, shared link).
  const gameData = useMemo(() => {
    if (location.state?.game) return location.state.game;
    if (!gameId) return null;
    return getGameById(gameId) ?? null;
  }, [location.state?.game, gameId, getGameById]);
  const isContinuousGame = location.state?.isContinuous;
  const continuousGameUrl = location.state?.gameUrl;

  // IS-INV-034: edition suffix derived from backend-provided isPremium (never auth.has())
  // Gate on isPremiumLoaded to avoid "Basic Edition" flicker before fetch resolves
  const editionSuffix = isPremiumLoaded && gameData?.challengeType === 'auto-challenge'
    ? (isPremium ? ' \u2013 Premium Edition' : ' \u2013 Basic Edition')
    : '';

  // Help text from game configuration (index signature covers this field)
  const helpText = typeof gameData?.configuration?.helpText === 'string'
    ? gameData.configuration.helpText.trim() || null
    : null;

  // Enable mock mode for testing
  const isMockMode = import.meta.env.VITE_MOCK_GAME_FRAME === 'true';

  // Check if challenge is completed (for routing to summary view)
  useEffect(() => {
    const checkCompletion = async () => {
      // Skip completion check for continuous games (they don't have completable challenges)
      if (isContinuousGame) {
        setIsCheckingCompletion(false);
        setIsChallengeCompleted(false);
        return;
      }

      // Skip check if missing required params, or if no auth signal at all (no Clerk session and no guest token)
      if (!gameId || !challengeId || (!isSignedIn && !guestSession.guestToken)) {
        setIsCheckingCompletion(false);
        setIsChallengeCompleted(false);
        return;
      }

      try {
        let isCompleted: boolean;
        let activityState: string | undefined;

        if (isSignedIn) {
          const progress = await apiClient.getChallengeProgress(gameId, challengeId);
          activityState = progress.activityState;
          isCompleted = activityState === 'completed';
        } else {
          // Guest path: getProgress returns the game's challenge list scoped to the guest token.
          // IMA-466 added guest-side progress tracking; reuse it for entry routing.
          const challengeIdNum = parseInt(challengeId, 10);
          const challenges = await apiClient.getProgress(gameId, guestSession.guestToken ?? undefined);
          activityState = challenges.find(c => c.id === challengeIdNum)?.progress?.activityState;
          isCompleted = activityState === 'completed';
        }

        setIsChallengeCompleted(isCompleted);

        console.log('[GameFrame] Challenge completion check:', {
          gameId,
          challengeId,
          isCompleted,
          activityState,
          isSignedIn
        });
      } catch (error) {
        console.error('[GameFrame] Failed to check challenge completion:', error);
        // On error, default to not completed (show gameplay)
        setIsChallengeCompleted(false);
      } finally {
        setIsCheckingCompletion(false);
      }
    };

    checkCompletion();
  }, [gameId, challengeId, isSignedIn, isContinuousGame, guestSession.guestToken]);

  // Reset completion state when navigating to a new challenge (IMA-466)
  // hasDetectedCompletion is component-level state; without this reset it stays true
  // across challenge A → challenge B transitions while GameFrame remains mounted.
  useEffect(() => {
    setShowCompletionPopup(false);
    setHasDetectedCompletion(false);
    setAuthSent(false);
  }, [gameId, challengeId]);

  // T5/T6: Poll progress endpoint to detect guest game completion (IMA-466)
  // Only polls while: anonymous user, game active (authSent), not yet completed
  const { data: guestProgressData } = useQuery({
    queryKey: ['guestGameProgress', gameId, challengeId, guestSession.guestToken],
    queryFn: async () => {
      if (!gameId) throw new Error('No gameId');
      return apiClient.getProgress(gameId, guestSession.guestToken ?? undefined);
    },
    enabled: !isSignedIn && isLoaded === true && authSent && !!gameId && !!challengeId && !hasDetectedCompletion,
    refetchInterval: 5000,
  });

  // Show completion popup when poll detects the current challenge as completed.
  // hasDetectedCompletion permanently stops both the poll and this effect on detection,
  // so dismissing the popup (setShowCompletionPopup(false)) cannot re-trigger it.
  useEffect(() => {
    if (!guestProgressData || !challengeId || hasDetectedCompletion) return;
    const challengeIdNum = parseInt(challengeId, 10);
    const match = guestProgressData.find(c => c.id === challengeIdNum);
    if (match?.progress?.activityState === 'completed') {
      setHasDetectedCompletion(true);
      setShowCompletionPopup(true);
    }
  }, [guestProgressData, challengeId, hasDetectedCompletion]);

  // Determine the actual game URL based on game data and completion status
  const determineGameUrl = () => {
    if (gameUrl) return gameUrl; // Custom URL provided

    // iframeSrc override: post-subscribe redirect from DMO flow encodes the game deep-link here.
    // Validate origin against the game registry before using as iframe src.
    const rawIframeSrc = searchParams.get('iframeSrc');
    if (rawIframeSrc) {
      try {
        const url = new URL(rawIframeSrc);
        if (isOriginAllowed(url.origin, gameData ? [gameData] : undefined)) {
          return rawIframeSrc;
        }
        console.warn('[Security] iframeSrc origin rejected', { origin: url.origin });
      } catch {
        console.warn('[Security] iframeSrc is not a valid URL');
      }
    }

    // For continuous games, use the URL from state
    if (isContinuousGame && continuousGameUrl) {
      console.log('[GameFrame] Using continuous game URL:', continuousGameUrl);
      return continuousGameUrl;
    }
    
    if (!gameData?.urlPattern?.frontend) return null; // No URL available
    if (!gameId || !challengeId) return null; // Ensure params are present

    // Build URL from game data for regular games
    const baseUrl = gameData.urlPattern.frontend;
    
    // Use route-based URLs for all games
    // LOW-008: Encode challengeId to prevent URL injection
    const route = isChallengeCompleted ? 'summary' : 'play';
    return `${baseUrl}/${route}/${encodeURIComponent(challengeId)}`;
  };

  // Get the resolved game URL
  const resolvedGameUrl = determineGameUrl();

  useEffect(() => {
    // Wait for registry to finish loading before declaring the game unavailable.
    // This covers direct URL / refresh cases where gameData comes from the registry fetch.
    if (!location.state?.game && gamesLoading) return;

    // Check if we have a valid game URL
    if (!resolvedGameUrl && !isMockMode) {
      setIsGameLoading(false);
      setGameError('This game is not available yet. Please check back later.');
      return;
    }

    // URL available — recover from prior error state (race: error fired before registry loaded)
    setIsGameLoading(true);
    setGameError(null);

    // Wait for completion check to finish before loading game
    if (isCheckingCompletion) {
      return; // Still checking completion status
    }

    // Simulate game loading
    const loadTimer = setTimeout(() => {
      setIsGameLoading(false);
    }, 1000);

    return () => clearTimeout(loadTimer);
  }, [gameId, resolvedGameUrl, isMockMode, isCheckingCompletion, gamesLoading, location.state?.game]);

  // Get user display name
  const userDisplayName = user?.firstName || user?.username || 'Player';
  const userInitials = user?.firstName && user?.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`
    : user?.firstName
    ? user.firstName.slice(0, 2).toUpperCase()
    : 'U';

  // Hide the main header when GameFrame is mounted
  useEffect(() => {
    // Add a class to the body to hide the main header
    document.body.classList.add('game-frame-active');
    
    return () => {
      // Remove the class when unmounting
      document.body.classList.remove('game-frame-active');
    };
  }, []);

  // Handle authentication handshake with game
  useEffect(() => {
    if (!iframeRef.current || isMockMode) {
      return;
    }

    const handleGameReady = async (event: MessageEvent): Promise<boolean> => {
      try {
        // Log that we received READY message
        console.log('[GameFrame] Game sent READY message from:', event.origin);

        // Guard: Clerk still restoring auth state — stash event and wait.
        // isSignedIn is false while isLoaded is false; acting now sends wrong token.
        if (!isLoaded) {
          pendingReadyEvent.current = event;
          return false;
        }

        // Get authentication token — Clerk path for signed-in users, guest path for anonymous
        const token = isSignedIn
          ? await getAuthToken(getToken)
          : guestSession.guestToken;

        if (!token) {
          if (!guestSession.isReady) {
            pendingReadyEvent.current = event; // stash — replayed when guestToken arrives
            return false;
          }
          setGameError('Unable to start game session. Please try again.');
          return false;
        }

        // Send AUTH_TOKEN message to game
        console.log('[GameFrame] Sending AUTH_TOKEN to game');
        sendMessageToGame(iframeRef.current!, {
          type: MessageType.AUTH_TOKEN,
          token
        }, event.origin);

        // T6: Track anonymous game start (IMA-466)
        if (!isSignedIn && guestSession.guestToken) {
          Analytics.trackAnonymousGameStart({
            gameId: gameId ?? '',
            challengeId: challengeId ?? ''
          });
        }

        setAuthSent(true);
        return true;
      } catch (error) {
        console.error('[GameFrame] Failed to send auth token:', error);
        setGameError('Authentication failed. Please try again.');
        return false;
      }
    };

    // Create message handler with origin validation and source validation
    const messageHandler = createMessageHandler(
      handleGameReady,
      gameData ? [gameData] : undefined,
      iframeRef.current?.contentWindow,
      (gameReturnTo?: string) => {
        // IS-INV-002: hub navigates itself — game cannot touch top-level navigation (IS-INV-008)
        const hubPath = window.location.pathname + window.location.search;
        const params = new URLSearchParams();
        params.set('returnTo', hubPath);

        if (gameReturnTo) {
          try {
            const url = new URL(gameReturnTo);
            if (isOriginAllowed(url.origin, gameData ? [gameData] : undefined)) {
              params.set('gameReturn', gameReturnTo);
            } else {
              console.warn('[Security] NAVIGATE_TO_SUBSCRIPTION: gameReturnTo origin rejected', { origin: url.origin });
            }
          } catch {
            console.warn('[Security] NAVIGATE_TO_SUBSCRIPTION: gameReturnTo is not a valid URL');
          }
        }

        navigate(`/subscription?${params.toString()}`);
      },
    );

    // Add event listener
    window.addEventListener('message', messageHandler);

    // If Clerk finished loading after READY already arrived, replay the stashed event.
    // Only clear the stash when AUTH_TOKEN was actually sent; preserve it for retry
    // on the next effect run (triggered when guestToken becomes available).
    if (isLoaded && pendingReadyEvent.current && !authSent) {
      void handleGameReady(pendingReadyEvent.current).then((sent) => {
        if (sent) pendingReadyEvent.current = null;
      });
    }

    return () => {
      window.removeEventListener('message', messageHandler);
    };
  }, [getToken, isMockMode, gameData, isSignedIn, isLoaded, guestSession.guestToken, guestSession.isReady, gameId, challengeId, authSent, navigate]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      {/* Game Frame Header */}
      <header 
        className={`bg-surface border-b border-border flex-shrink-0 transition-all duration-300 relative ${
          isHeaderCollapsed ? 'h-2' : 'h-16'
        }`}
      >
        <div className="h-full px-4 md:px-6 flex items-center justify-between">
          {!isHeaderCollapsed && (
            <>
              {/* Left Section - Navigation */}
              <div className="flex items-center gap-2 md:gap-3">
                {/* Back to Hub Button */}
                <Link
                  to="/"
                  className="flex items-center text-muted-foreground hover:text-foreground
                           transition-colors duration-200"
                  aria-label="Back to Game Hub"
                >
                  <img
                    src="/logos/is-games-logo.svg"
                    alt="IS Games"
                    className="h-5 md:h-7 w-auto dark:hidden"
                  />
                  <img
                    src="/logos/is-games-logo-dark.svg"
                    alt="IS Games"
                    className="h-5 md:h-7 w-auto hidden dark:block"
                  />
                </Link>

                {/* Game Title (if available) */}
                {gameData && (
                  <>
                    <span className="text-muted-foreground text-lg md:text-[22px]">|</span>
                    <h1 className="font-bebas-neue text-[19px] md:text-[22px] tracking-wide uppercase text-foreground">
                      {gameData.displayName}{editionSuffix}
                    </h1>
                  </>
                )}
              </div>

              {/* Center Section - Placeholder for future additions */}
              <div className="flex-1 flex justify-center">
                {/* This area reserved for game-specific controls, scores, timers, etc. */}
              </div>

              {/* Right Section - Help button + User Profile / Guest CTA */}
              <div className="flex items-center gap-3">
                {helpText && (
                  <button
                    onClick={() => setIsHelpOpen(true)}
                    aria-label="How to play"
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  >
                    <HelpCircle className="h-5 w-5" />
                  </button>
                )}
                {/* T5: Guest conversion CTA (IMA-466) */}
                <GuestConversionCTA gameId={gameId ?? ''} />
                {isLoaded && isSignedIn && user && (
                  <>
                    <span className="hidden md:block text-sm text-foreground font-emphasis">
                      {userDisplayName}
                    </span>
                    <div className="w-10 h-10 rounded-full bg-sport-accent text-white font-semibold
                                  flex items-center justify-center">
                      {user.imageUrl ? (
                        <img 
                          src={user.imageUrl} 
                          alt={user.fullName || 'User avatar'} 
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        userInitials
                      )}
                    </div>
                  </>
                )}
              </div>
            </>
          )}

        </div>
        
        {/* Collapse/Expand Toggle Button - Centered on bottom border */}
        <button
          onClick={() => setIsHeaderCollapsed(!isHeaderCollapsed)}
          className="absolute left-1/2 -translate-x-1/2 -bottom-3 
                     bg-surface hover:bg-muted border border-border rounded-full p-2 
                     text-muted-foreground hover:text-foreground transition-all duration-200 
                     z-10 shadow-sm hover:shadow-md"
          aria-label={isHeaderCollapsed ? 'Expand header' : 'Collapse header'}
          title={isHeaderCollapsed ? 'Expand header' : 'Collapse header'}
        >
          {isHeaderCollapsed ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronUp className="w-4 h-4" />
          )}
        </button>
      </header>

      {/* Game Content Area */}
      <div className="flex-1 relative overflow-hidden">
        {/* Loading State */}
        {isGameLoading && (
          <div className="absolute inset-0 bg-background flex items-center justify-center z-10">
            <div className="text-center">
              <LoadingSpinner className="mb-4" />
              <p className="text-muted-foreground font-emphasis text-sm uppercase-tracked">
                Loading Game...
              </p>
            </div>
          </div>
        )}

        {/* Error State */}
        {gameError && (
          <div className="absolute inset-0 bg-background flex items-center justify-center z-10">
            <div className="text-center max-w-md mx-auto px-4">
              <div className="text-6xl mb-4">🚧</div>
              <h2 className="font-display text-2xl text-foreground mb-3">
                {gameData?.displayName || 'Game'} Coming Soon
              </h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {gameError}
              </p>
              
              {/* Additional Context */}
              <div className="bg-surface rounded-lg p-4 mb-6 text-left">
                <p className="text-muted-foreground text-sm">
                  <span className="font-medium">Game:</span> {gameData?.displayName || gameId}
                </p>
                {challengeId && (
                  <p className="text-muted-foreground text-sm mt-1">
                    <span className="font-medium">Challenge:</span> #{challengeId}
                  </p>
                )}
                <p className="text-muted-foreground text-sm mt-1">
                  <span className="font-medium">Status:</span> Under Development
                </p>
              </div>

              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-sport-accent text-white
                         rounded-md font-emphasis text-sm font-medium uppercase-tracked
                         hover:transform hover:-translate-y-0.5 transition-all duration-200"
              >
                <ChevronLeft className="w-4 h-4" />
                Back to Hub
              </Link>
            </div>
          </div>
        )}

        {/* T5: Post-game conversion popup for anonymous users (IMA-466) */}
        <GuestCompletionPopup
          isOpen={showCompletionPopup}
          onDismiss={() => setShowCompletionPopup(false)}
        />

        {/* Game iframe */}
        {isMockMode ? (
          // Mock Content for Testing
          <div className="w-full h-full flex items-center justify-center bg-surface">
            <div className="text-center max-w-2xl mx-auto px-4">
              <div className="text-6xl mb-4">🎮</div>
              <h2 className="font-display text-2xl text-foreground mb-2">
                Mock Game Frame
              </h2>
              <p className="text-muted-foreground text-sm mb-4">
                This is a mock game frame for testing authentication handshake
              </p>
              
              {/* Game Context */}
              <div className="bg-background rounded-lg p-4 mb-4 text-left">
                <h3 className="font-emphasis text-sm font-medium uppercase-tracked mb-2">
                  Game Context
                </h3>
                <p className="text-muted-foreground text-sm">
                  <span className="font-medium">Game ID:</span> {gameId || 'test-game'}
                </p>
                {challengeId && (
                  <p className="text-muted-foreground text-sm">
                    <span className="font-medium">Challenge ID:</span> {challengeId}
                  </p>
                )}
                {isSignedIn && (
                  <p className="text-muted-foreground text-sm">
                    <span className="font-medium">Player:</span> {userDisplayName}
                  </p>
                )}
              </div>

              {/* Authentication Status */}
              <div className="bg-background rounded-lg p-4 text-left">
                <h3 className="font-emphasis text-sm font-medium uppercase-tracked mb-2">
                  Authentication Status
                </h3>
                {!isSignedIn ? (
                  <p className="text-warning text-sm">
                    ⚠️ Not signed in - Authentication disabled
                  </p>
                ) : authSent ? (
                  <div>
                    <p className="text-success text-sm mb-2">
                      ✅ Authentication handshake complete
                    </p>
                    <p className="text-muted-foreground text-xs">
                      Auth token sent to game via postMessage
                    </p>
                    {import.meta.env.VITE_MOCK_AUTH === 'true' && (
                      <p className="text-muted-foreground text-xs mt-1">
                        (Using mock JWT token for testing)
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    ⏳ Waiting for game to send READY message...
                  </p>
                )}
              </div>

              {/* Instructions */}
              <div className="mt-6 text-sm text-muted-foreground">
                <p>To test the authentication handshake:</p>
                <ol className="text-left mt-2 space-y-1">
                  <li>1. Open browser console to see messages</li>
                  <li>2. Set VITE_LOG_GAME_MESSAGES=true for detailed logs</li>
                  <li>3. Load a real game to see the handshake in action</li>
                </ol>
              </div>
            </div>
          </div>
        ) : resolvedGameUrl ? (
          /*
            Security Note: This sandbox configuration is safe because games are deployed
            to separate Vercel projects (different origins) than the hub. The combination
            of allow-scripts and allow-same-origin would be dangerous if games were on
            the same origin, as it would allow sandbox escape. Always ensure games are
            deployed to different domains/subdomains.
            - allow-scripts: Required for game JavaScript execution
            - allow-same-origin: Required for postMessage authentication
            - allow-forms: Required for game form submissions
            - allow-popups: Required for game popup windows
            - allow-popups-to-escape-sandbox: Allows external links opened by the game
              (e.g. Baseball Reference player pages) to open as normal tabs without
              inheriting sandbox restrictions. Safe: only new windows are affected —
              the hub page itself is never navigated (IS-INV-008).
          */
          <iframe
            ref={iframeRef}
            src={resolvedGameUrl}
            className="w-full h-full border-0"
            title={gameData?.displayName || 'Game'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
            onLoad={() => setIsGameLoading(false)}
            onError={() => {
              setIsGameLoading(false);
              setGameError('Unable to connect to the game server. Please try again later.');
            }}
          />
        ) : null}
      </div>

      {/* Footer - hub-provided, same pattern as header */}
      {/* Add bottom padding on mobile to account for BottomNav overlay */}
      <div className="pb-[50px] md:pb-0">
        <Footer />
      </div>

      {/* Help modal - renders via Radix Portal (z-50), outside iframe */}
      {helpText && (
        <BaseModal
          isOpen={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
          title={`How to play ${gameData?.displayName ?? 'this game'}`}
          size="md"
          showCloseButton={true}
        >
          <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
            {helpText}
          </p>
        </BaseModal>
      )}
    </div>
  );
}